import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Button,
  Checkbox,
  Flex,
  Paper,
  rem,
  Select,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { randomId } from "@mantine/hooks";
import {
  IconGripVertical,
  IconPlus,
  IconTrash,
  IconCopy,
} from "@tabler/icons-react";
import { memo, useCallback } from "react";

interface Props {
  onRemove: (id: string) => void;
  item: any;
  index: number;
  onChange: (index: number, val: any, property: string) => void;
  onCopy: (item: any) => void;
}

const fieldTypes = [
  { label: "Check Box", value: "check" },
  { label: "Text Input", value: "text" },
  { label: "Date Picker", value: "date" },
  { label: "Date/Time Picker", value: "datetime" },
];

const CheckListSubField = ({
  field,
  provided,
  updateField,
  removeField,
}: any) => (
  <Flex
    gap="xs"
    align="center"
    mb="xs"
    ref={provided.innerRef}
    {...provided.draggableProps}
    style={provided.draggableProps.style}>
    <div
      {...provided.dragHandleProps}
      style={{
        display: "flex",
        alignItems: "center",
        cursor: "grab",
      }}>
      <IconGripVertical size={18} />
    </div>
    <TextInput
      placeholder="Field Label"
      defaultValue={field.text}
      onBlur={e => updateField(field.id, "text", e.target.value)}
      style={{ flex: 1 }}
    />
    <Select
      data={fieldTypes}
      value={field.unit}
      onChange={val => updateField(field.id, "unit", val)}
      w={170}
    />
    <Tooltip label="If checked, user will be able to set N/A state while filling the checklist">
      <Flex direction="column" mb={7}>
        <Text size="xs">N/A</Text>
        <Checkbox
          checked={field.naAvailable}
          onChange={e =>
            updateField(field.id, "naAvailable", e.target.checked)
          }
        />
      </Flex>
    </Tooltip>
    <ActionIcon
      color="red"
      variant="subtle"
      onClick={() => removeField(field.id)}>
      <IconTrash size={16} />
    </ActionIcon>
  </Flex>
);

const CheckListFieldItem = memo(
  ({ item, onRemove, index, onChange, onCopy }: Props) => {
    const handleRemove = useCallback(() => {
      onRemove(item.id);
    }, [item.id, onRemove]);

    const addField = () => {
      const fields = item.fields || [];
      onChange(
        index,
        [...fields, { id: randomId(), text: "", unit: "check" }],
        "fields",
      );
    };

    const handleCopy = useCallback(() => {
      onCopy(item);
    }, [item, onCopy]);

    const updateField = (id: string, prop: string, value: any) => {
      const fields = item.fields.map((f: any) =>
        f.id === id ? { ...f, [prop]: value } : f,
      );
      onChange(index, fields, "fields");
    };

    const removeField = (id: string) => {
      const fields = item.fields.filter((f: any) => f.id !== id);
      onChange(index, fields, "fields");
    };

    const onDragEnd = useCallback(
      ({ destination, source }: any) => {
        if (!destination) return;
        const fields = Array.from(item.fields || []);
        const [reorderedItem] = fields.splice(source.index, 1);
        fields.splice(destination.index, 0, reorderedItem);
        onChange(index, fields, "fields");
      },
      [item.fields, onChange, index],
    );

    return (
      <Draggable key={item.id} index={index} draggableId={item.id}>
        {provided => (
          <Flex
            ref={provided.innerRef}
            {...provided.draggableProps}
            gap="xs"
            justify={"center"}
            align="stretch"
            direction="column"
            className="draggable"
            mb="sm">
            <Flex
              gap="xs"
              justify={"center"}
              align="end"
              style={{ width: "100%" }}>
              <Flex
                {...provided.dragHandleProps}
                justify="center"
                align="center"
                mb={20}>
                <IconGripVertical
                  style={{ width: rem(22), height: rem(22) }}
                  stroke={1.5}
                />
              </Flex>
              <Textarea
                style={{ flex: 1 }}
                label="Name"
                autosize
                defaultValue={item.text}
                onBlur={e => onChange(index, e.target.value, "text")}
              />
              <Flex align="flex-end" gap="xs">
                <Select
                  label="Unit"
                  data={[
                    { label: "Check Box", value: "check" },
                    { label: "Text Input", value: "text" },
                    { label: "Header", value: "header" },
                    { label: "Date Picker", value: "date" },
                    { label: "Date/Time Picker", value: "datetime" },
                    { label: "Multiple Inputs", value: "multiple" },
                  ]}
                  styles={{
                    input: { minHeight: "42px" },
                  }}
                  value={item.unit}
                  onChange={v => onChange(index, v, "unit")}
                />
                {item.unit !== "header" && item.unit !== "multiple" ? (
                  <Tooltip label="If checked, user will be able to set N/A state while filling the checklist">
                    <Flex direction="column" mb={7}>
                      <Text size="xs">N/A</Text>
                      <Checkbox
                        checked={item.naAvailable}
                        onChange={e =>
                          onChange(index, e.target.checked, "naAvailable")
                        }
                      />
                    </Flex>
                  </Tooltip>
                ) : (
                  <div style={{ width: 21.5 }}>
                    {item.unit === "multiple" ? (
                      <ActionIcon color="main" onClick={handleCopy} mb={7}>
                        <IconCopy />
                      </ActionIcon>
                    ) : null}
                  </div>
                )}

                <ActionIcon color="red" onClick={handleRemove} mb={7}>
                  <IconTrash />
                </ActionIcon>
              </Flex>
            </Flex>

            {item.unit === "multiple" && (
              <Paper
                withBorder
                p="xs"
                w="calc(100% - 32px)"
                ml={32}
                style={{ alignSelf: "flex-start" }}>
                <Text size="sm" fw={500} mb="xs">
                  Fields
                </Text>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable
                    droppableId={`droppable-${item.id}`}
                    renderClone={(provided, snapshot, rubric) => (
                      <CheckListSubField
                        field={item.fields[rubric.source.index]}
                        provided={provided}
                        updateField={updateField}
                        removeField={removeField}
                      />
                    )}>
                    {provided => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {(item.fields || []).map((field: any, i: number) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={i}>
                            {provided => (
                              <CheckListSubField
                                field={field}
                                provided={provided}
                                updateField={updateField}
                                removeField={removeField}
                              />
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <Button
                  variant="light"
                  size="xs"
                  leftIcon={<IconPlus size={14} />}
                  onClick={addField}>
                  Add Field
                </Button>
              </Paper>
            )}
          </Flex>
        )}
      </Draggable>
    );
  },
  (prev, next) => prev.item === next.item && prev.index === next.index,
);

export default CheckListFieldItem;
